import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import bcrypt from 'bcryptjs';
import supabase from '../lib/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Força desativar bypass de autenticação para produção.
  // Caso precise reativar para testes locais, altere para `true` temporariamente
  // ou volte a ler da env: process.env.REACT_APP_BYPASS_AUTH === 'true'
  const bypassAuth = false;

  const getMockUser = () => ({
    id: 'mock-user',
    name: 'Usuário Demo',
    email: 'demo@hadritech.com.br',
    role: 'admin',
  });

  const fetchUser = React.useCallback(async () => {
    console.log('=== Iniciando fetchUser ===');
    if (bypassAuth) {
      console.log('Bypass de autenticação habilitado. Pulando chamada à API.');
      const existing = localStorage.getItem('user');
      if (existing) {
        try {
          setCurrentUser(JSON.parse(existing));
        } catch (_) {
          localStorage.removeItem('user');
          setCurrentUser(getMockUser());
          localStorage.setItem('user', JSON.stringify(getMockUser()));
        }
      } else {
        setCurrentUser(getMockUser());
        localStorage.setItem('user', JSON.stringify(getMockUser()));
      }
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    console.log('Token no fetchUser:', token ? 'Presente' : 'Ausente');
    
    if (!token) {
      console.log('Nenhum token encontrado no localStorage');
      setLoading(false);
      return;
    }

    try {
      console.log('Fazendo requisição para /auth/user');
      const resp = await api.get('/auth/user');
      const userData = resp?.data ?? resp;
      console.log('Resposta de /auth/user:', userData);
      setCurrentUser(userData);
      console.log('Usuário definido no estado:', userData);
    } catch (error) {
      console.error('Erro ao buscar usuário:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      localStorage.removeItem('token');
      setCurrentUser(null);
    } finally {
      console.log('=== Finalizando fetchUser ===');
      setLoading(false);
    }
  }, [bypassAuth]); // Removido api das dependências pois é uma importação estática

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (bypassAuth) {
      // Em bypass, força um usuário no estado e localStorage
      const mock = getMockUser();
      setCurrentUser(mock);
      localStorage.setItem('user', JSON.stringify(mock));
      setLoading(false);
      return;
    }

    if (token && userData) {
      try {
        // Tenta analisar os dados do usuário do localStorage
        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);
        
        // Atualiza os dados do usuário a partir da API
        fetchUser().catch(err => {
          console.error('Erro ao buscar dados do usuário:', err);
          // Se falhar, mantém os dados do localStorage
        });
      } catch (error) {
        console.error('Erro ao analisar dados do usuário:', error);
        // Se houver erro, limpa os dados inválidos
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    
    setLoading(false);
  }, [fetchUser, bypassAuth]);

  // Login user
  const login = async (email, password) => {
    console.log('=== Iniciando login (Supabase) ===');
    console.log('Email:', email);

    try {
      setError('');

      if (bypassAuth) {
        console.log('Bypass de autenticação: efetuando login local sem API');
        const mock = getMockUser();
        localStorage.setItem('user', JSON.stringify(mock));
        setCurrentUser(mock);
        return { success: true };
      }

      if (!supabase) {
        const msg = 'Supabase não configurado. Verifique as variáveis de ambiente.';
        console.error(msg);
        setError(msg);
        return { success: false, error: msg };
      }

      console.log('Buscando usuário no Supabase...');
      const { data, error: supabaseError } = await supabase
        .from('usuarios')
        .select('*')
        .eq('email', email)
        .single();

      if (supabaseError) {
        console.error('Erro ao buscar usuário no Supabase:', supabaseError);
        const msg = 'Erro ao acessar o serviço de autenticação.';
        setError(msg);
        return { success: false, error: msg };
      }

      if (!data) {
        const msg = 'Usuário não encontrado.';
        console.warn(msg);
        setError(msg);
        return { success: false, error: msg };
      }

      const senhaHash = data.senha_hash;
      const senhaConfere = await bcrypt.compare(password, senhaHash || '');

      if (!senhaConfere) {
        const msg = 'Senha incorreta.';
        console.warn(msg);
        setError(msg);
        return { success: false, error: msg };
      }

      const user = {
        id: data.id,
        nome: data.nome,
        email: data.email,
        role: 'user',
      };

      localStorage.setItem('user', JSON.stringify(user));
      // Mantém compatibilidade com fluxo existente que usa token opcional
      localStorage.setItem('token', 'supabase-local-token');
      setCurrentUser(user);
      console.log('Autenticação realizada com sucesso via Supabase');
      return { success: true };
    } catch (error) {
      console.error('Erro inesperado no login Supabase:', error);
      const message = error.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Register user
  const register = async (name, email, password) => {
    try {
      setError('');
      if (bypassAuth) {
        const mock = getMockUser();
        localStorage.setItem('user', JSON.stringify(mock));
        setCurrentUser(mock);
        return { success: true };
      }
      const response = await api.post('/auth/register', { name, email, password });
      const { token } = response.data;
      localStorage.setItem('token', token);
      await fetchUser();
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
